import React from 'react';

/**
 * ProcedureStartConfirmDialog — Phase 4.0j patient-identity guard.
 *
 * Inserted between the chip's "Start" tap and the underlying
 * `executeSitting` action. The assignee must read the patient name +
 * queue number + procedure aloud (in their head, anyway) and tap an
 * affirmative button before the sitting transitions to in_progress.
 *
 * The cognitive friction is the point: in a busy clinic where one nurse
 * has Ramesh and Ram both in their list, a silent Start is the moment
 * mistakes happen. Two seconds of forced attention catches almost all
 * of them.
 *
 * 60-second sessionStorage suppression (handled by useSittingActions)
 * means a nurse who confirmed a sitting and immediately taps Start
 * again (or has already opened the chip drawer) doesn't get banner-
 * blind from repeated modals.
 *
 * Tagged `data-keep-bubble-open="1"` so it doesn't trip the
 * RoleTaskBadge's click-outside collapse.
 */
function formatScheduled(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    const sameDay =
      d.getFullYear() === today.getFullYear()
      && d.getMonth() === today.getMonth()
      && d.getDate() === today.getDate();
    const time = d.toLocaleTimeString('en-IN', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
    if (sameDay) return `today · ${time}`;
    const date = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    return `${date} · ${time}`;
  } catch {
    return null;
  }
}

function formatCurrency(amount) {
  if (amount == null || amount === '') return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export default function ProcedureStartConfirmDialog({ sitting, onConfirm, onCancel }) {
  const [busy, setBusy] = React.useState(false);

  const procedureName =
    sitting?.procedure_name
    || sitting?.procedure_snapshot?.name
    || 'Procedure';
  const patientName = sitting?.patient_name || 'Unknown patient';
  const queueNumber = sitting?.queue_number;
  const doctorName = sitting?.visit_doctor_name || sitting?.doctor_name;
  const scheduledLine = formatScheduled(sitting?.scheduled_for);
  const cost = formatCurrency(sitting?.final_cost ?? sitting?.estimated_cost);

  const firstName = patientName.split(' ')[0];

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Confirm patient ${patientName} for ${procedureName}`}
      data-keep-bubble-open="1"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        background: 'rgba(15,23,42,0.62)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={(e) => {
        // Don't dismiss on backdrop tap — assignee MUST pick a button.
        if (e.target === e.currentTarget) e.stopPropagation();
      }}
    >
      <div
        className="bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100"
        style={{
          width: 'min(440px, 100%)',
          borderRadius: 16,
          boxShadow: '0 28px 72px rgba(0,0,0,0.45)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid rgba(15,23,42,0.12)',
        }}
      >
        <header
          className="bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
          style={{
            padding: '0.75rem 1.125rem',
            borderBottom: '1px solid rgba(180,83,9,0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span aria-hidden="true" style={{ fontSize: '1.25rem' }}>⚠️</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 700 }}>
              Confirm patient before starting
            </div>
            <div style={{ fontSize: '0.6875rem', opacity: 0.85 }}>
              Read the name and queue number — wrong patient cannot be undone.
            </div>
          </div>
        </header>

        <div style={{ padding: '1.125rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Procedure */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span aria-hidden="true" style={{ fontSize: '1.125rem' }}>💉</span>
            <span style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.25, flex: 1 }}>
              {procedureName}
            </span>
            {cost && (
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, opacity: 0.85 }}>
                {cost}
              </span>
            )}
          </div>

          {/* Patient — primary identity. Big + bold + queue tag inline. */}
          <div
            className="bg-slate-50 dark:bg-slate-800"
            style={{
              padding: '0.75rem 0.875rem',
              borderRadius: 12,
              border: '1px solid rgba(15,23,42,0.12)',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.7 }}>
              Patient
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 }}>
                {patientName}
              </span>
              {queueNumber != null && (
                <span
                  className="bg-teal-600 text-white"
                  style={{
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    padding: '0.125rem 0.5rem',
                    borderRadius: 9999,
                    letterSpacing: '0.04em',
                  }}
                >
                  Q-{queueNumber}
                </span>
              )}
            </div>
            {(doctorName || scheduledLine) && (
              <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: 2 }}>
                {doctorName && <>🩺 {doctorName}</>}
                {doctorName && scheduledLine && <> · </>}
                {scheduledLine && <>{scheduledLine}</>}
              </div>
            )}
          </div>
        </div>

        <footer
          className="bg-slate-50 dark:bg-slate-800"
          style={{
            padding: '0.75rem 0.875rem',
            borderTop: '1px solid rgba(15,23,42,0.10)',
            display: 'flex',
            gap: 8,
            justifyContent: 'space-between',
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="bg-white text-slate-800 hover:bg-slate-100
                       dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-700
                       border-rose-300 dark:border-rose-700"
            style={{
              padding: '0.5rem 0.875rem',
              borderRadius: 8,
              borderWidth: 1,
              borderStyle: 'solid',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            ✕ Wrong patient
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className="bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
            style={{
              padding: '0.5rem 1rem',
              borderRadius: 8,
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: 700,
              cursor: busy ? 'not-allowed' : 'pointer',
              flex: 1,
              maxWidth: '60%',
            }}
          >
            {busy ? 'Starting…' : `✓ Yes, this is ${firstName}`}
          </button>
        </footer>
      </div>
    </div>
  );
}
